// Ultimate Card Game (UCG) - Screen <-> World Camera
//
// The simulation is always exactly WORLD_W x WORLD_H (matches Battlefield's
// fixed 1600x540 - see js/engine/battlefield.js). That can never change per
// device: online play is deterministic lockstep, so a phone and a desktop
// in the same match must compute identical spawn offsets, ranges and splash
// radii. This module is the ONLY place screen pixels and world pixels meet -
// it owns the canvas backing store size and the camera, and is read-only
// with respect to simulation state.
//
// Two fit modes:
//   'contain' (desktop + mobile landscape) - the whole field fits on
//     screen, centered, with letterbox bars above/below if the device is
//     taller (relative to its width) than the field's own 1600:540 ratio.
//     No panning: there's nothing to pan to, the whole field is already
//     visible.
//   'cover-height' (mobile portrait) - the field is scaled to fill the
//     screen's height exactly, which crops most of its 1600px width. The
//     camera can then pan horizontally within the field, either by drag or
//     by auto-following the battlefield's territoryDivider.
export class Viewport {
  constructor(canvas, worldW, worldH) {
    this.canvas = canvas;
    this.worldW = worldW;
    this.worldH = worldH;

    this.cssW = 0;
    this.cssH = 0;
    this.dpr = 1;
    // Retuned per quality tier by js/engine/performance.js (2.0 normally,
    // 1.5 under sustained load) - see resize() below for where it's used.
    this.dprCap = 2;

    this.camera = { x: worldW / 2, y: worldH / 2, zoom: 1 };
    this.mode = 'contain';
    this.baseZoom = 1;
    // Two discrete Mobile Portrait zoom perspectives:
    //   State 0 (Default Close): standard 'cover-height' zoom (~37% of 1600px field visible)
    //   State 1 (Wide ~50% Overview): zoomed out so ~50% (800px) of the 1600px field is visible
    // Two discrete Mobile Landscape zoom perspectives:
    //   State 0 (Default & Max Overview): full 100% (1600px) field visible
    //   State 1 (Embedded Lane View): zoomed in (~70% field width) so the combat lane is embedded between Top-HUD and Bottom-HUD
    this.zoomClose = 1;
    this.zoomWide = 1;
    this.zoomOverview = 1;
    this.zoomEmbedded = 1;
    this.embeddedCameraY = worldH / 2;
    this.portraitZoomState = 0; // 0 = Default Close, 1 = Wide (~50% field width)
    this.landscapeZoomState = 0; // 0 = Default Overview (100%), 1 = Embedded Lane View (~70%)
    this.targetZoom = 1;
    this.isPinching = false;
    this._pinchStartZoom = 1;
    this._pinchStartState = 0;
    this._pinchLastCenterClientX = null;
    this._zoomAnchorClientX = null;
    this._landscapeFocusWorldX = null;
    this._lastMode = null;

    this.trackedUnit = null;
    this.trackingProgress = 0; // 0 = standard contain overview, 1 = fully focused tracking
    this._deadLingerTimer = 0;

    // Manual pan (portrait drag) suspends auto-follow for a few seconds so
    // a deliberate look-around isn't immediately yanked back to the front.
    this.userPanUntil = 0;
    this.mobileIntro = null;
    this._combatWeight = 0;

    this.resize();

    // ResizeObserver instead of (only) window's 'resize'/'orientationchange'
    // events: it fires on any actual layout change to the canvas's own
    // box - orientation flips, the mobile browser chrome collapsing/
    // expanding, even a same-size-window CSS layout shift - which is
    // exactly what actually needs a re-fit, without depending on the
    // window-level event always being dispatched.
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.resize());
      this._ro.observe(canvas);
    }
  }

  // Deliberately the SAME primitive that drives the @media (orientation:
  // portrait) rules in css/main.css, rather than comparing this.cssW/cssH
  // (the canvas element's own getBoundingClientRect) - on Chrome for iOS
  // specifically, the canvas's reported box has been observed to disagree
  // with the page's actual portrait layout (the HUD stacks correctly per
  // CSS, camera still picked 'contain' as if landscape). matchMedia asks
  // the browser's own orientation determination directly, so it can never
  // disagree with what the CSS on the same page just did.
  isPortrait() {
    if (typeof window !== 'undefined') {
      const w = window.innerWidth || this.cssW || 0;
      const h = window.innerHeight || this.cssH || 0;
      if (w > 0 && h > 0 && Math.abs(w - h) > 2) {
        return h >= w;
      }
      if (typeof document !== 'undefined' && document.documentElement?.classList?.contains) {
        if (document.documentElement.classList.contains('is-portrait')) return true;
        if (document.documentElement.classList.contains('is-landscape')) return false;
      }
      if (typeof window.matchMedia === 'function') {
        return window.matchMedia('(orientation: portrait)').matches;
      }
      if (typeof window.orientation === 'number') {
        return window.orientation === 0 || window.orientation === 180;
      }
      if (typeof screen !== 'undefined' && screen.orientation && screen.orientation.type) {
        return screen.orientation.type.startsWith('portrait');
      }
    }
    return this.cssH > this.cssW;
  }

  // Recompute backing store size and camera fit. Call on load, on
  // window resize, and on orientationchange.
  resize() {
    if (typeof window !== 'undefined') {
      try {
        window.scrollTo(0, 0);
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      } catch (e) {}
    }
    const rect = this.canvas.getBoundingClientRect();
    this.cssW = Math.max(1, rect.width);
    this.cssH = Math.max(1, rect.height);
    // Capped (2x normally, 1.5x under sustained load - see this.dprCap and
    // js/engine/performance.js): a phone's true 3x DPR would triple
    // fill-rate for barely perceptible sharpness gain on a screen this
    // small - see MOBILE_PLAN.md section 9.
    this.dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);

    const backingW = Math.round(this.cssW * this.dpr);
    const backingH = Math.round(this.cssH * this.dpr);
    if (this.canvas?._suspended) {
      return;
    }
    const sizeChanged = (this.canvas.width !== backingW || this.canvas.height !== backingH);
    if (sizeChanged) {
      this.canvas.width = backingW;
      this.canvas.height = backingH;
    }

    this.mode = this.isPortrait() ? 'cover-height' : 'contain';
    if (this._lastMode && this._lastMode !== this.mode) {
      this.portraitZoomState = 0;
      this.landscapeZoomState = 0;
      this.isPinching = false;
      this._zoomAnchorClientX = null;
      this._landscapeFocusWorldX = null;
    }
    this._lastMode = this.mode;

    if (this.mode === 'contain') {
      // The canvas runs full height behind the bottom dock, so the field is
      // centred in what's left above it. Which dock is showing is a LAYOUT
      // question (css/main.css: the 112px strip under the mobile-landscape query,
      // a compact clamp(170px, 24vh, 190px) dock on foldables/tablets in landscape,
      // and the 280px desktop dock on full desktop/laptop screens).
      const isMobLand = this.isMobileLandscape() && !this.isDesktop();
      let bottomHudH = isMobLand ? 112 : 280;
      let topHudH = isMobLand ? 52 : 64;
      if (!isMobLand && typeof document !== 'undefined' && typeof document.getElementById === 'function') {
        const bottomHud = document.getElementById('bottom-hud');
        if (bottomHud && typeof bottomHud.getBoundingClientRect === 'function') {
          const rect = bottomHud.getBoundingClientRect();
          if (rect && rect.height > 80 && rect.height < 320) {
            bottomHudH = Math.round(rect.height);
          }
        }
        const topHud = document.getElementById('top-hud');
        if (topHud && typeof topHud.getBoundingClientRect === 'function') {
          const tRect = topHud.getBoundingClientRect();
          if (tRect && tRect.height > 30 && tRect.height < 100) {
            topHudH = Math.round(tRect.height);
          }
        }
      }
      this.bottomHudH = bottomHudH;
      const visibleH = Math.max(1, this.cssH - bottomHudH);
      this.baseZoom = Math.min(this.cssW / this.worldW, visibleH / this.worldH);
      this.baseCameraX = this.worldW / 2;
      this.baseCameraY = this.worldH / 2 + (bottomHudH / 2) / this.baseZoom;

      const clearH = Math.max(120, this.cssH - bottomHudH - topHudH);
      // State 0 (Default & Max Overview): full 1600px field width visible.
      // State 1 (Embedded Lane View): combat lane (320px) embedded cleanly between #top-hud and #bottom-hud (~1.40x-1.47x baseZoom, ~68-72% field width).
      this.zoomOverview = this.baseZoom;
      this.zoomEmbedded = Math.max(
        this.baseZoom * 1.38,
        Math.min(this.cssW / (this.worldW * 0.68), clearH / 312)
      );
      this.zoomWide = this.zoomOverview;
      this.zoomClose = this.zoomEmbedded;
      const rawEmbeddedY = 326 + ((bottomHudH - topHudH) / 2) / this.zoomEmbedded;
      const minCamY = (this.cssH / 2) / this.zoomEmbedded;
      const maxCamY = this.worldH - ((this.cssH / 2 - bottomHudH) / this.zoomEmbedded);
      this.embeddedCameraY = (minCamY <= maxCamY)
        ? Math.min(maxCamY, Math.max(minCamY, rawEmbeddedY))
        : rawEmbeddedY;
      this.targetZoom = (this.landscapeZoomState === 1) ? this.zoomEmbedded : this.zoomOverview;
      if (!this.trackedUnit && this.trackingProgress <= 0.001) {
        if (!this.isPinching) {
          this.camera.zoom = this.targetZoom;
        }
        if (this.landscapeZoomState === 0 && !this.isPinching) {
          this.camera.x = this.baseCameraX;
          this.camera.y = this.baseCameraY;
        }
        this._updatePortraitPanBounds();
      } else {
        this.panMinX = this.panMaxX = this.worldW / 2;
      }
    } else {
      // Mobile portrait mode ('cover-height'):
      // State 0 (Default): 0.81x height-fit zoom gives the slightly wider default tactical view (~37% field width).
      // State 1 (Wide Overview): shows 50% (800 world px) of the 1600px battlefield width.
      this.zoomClose = (this.cssH / this.worldH) * 0.81;
      this.zoomWide = this.cssW / (this.worldW * 0.50);
      this.zoomOverview = this.zoomWide;
      this.zoomEmbedded = this.zoomClose;
      this.baseZoom = this.zoomClose;
      this.targetZoom = (this.portraitZoomState === 1) ? this.zoomWide : this.zoomClose;
      this.baseCameraX = this.worldW / 2;
      this.baseCameraY = this.worldH / 2 + 16;
      this.embeddedCameraY = this.baseCameraY;
      if (!this.trackedUnit && this.trackingProgress <= 0.001) {
        if (!this.isPinching) {
          this.camera.zoom = this.targetZoom;
        }
        this.camera.y = this.baseCameraY;
      }
      this._updatePortraitPanBounds();
      if (!this.trackedUnit && this.trackingProgress <= 0.001 && !this.mobileIntro && !this._hasPositionedPortrait) {
        this.camera.x = this.baseCameraX;
        this._hasPositionedPortrait = true;
      }
      if (this.mobileIntro) {
        this.mobileIntro.startX = Math.min(this.panMaxX, Math.max(this.panMinX, this.mobileIntro.startX));
        this.mobileIntro.targetX = Math.min(this.panMaxX, Math.max(this.panMinX, this.mobileIntro.targetX));
        this.camera.x = Math.min(this.panMaxX, Math.max(this.panMinX, this.camera.x));
      }
    }
    this.resetFollow();

    if (sizeChanged && this.onResize) {
      this.onResize();
    }
  }

  isLandscapeZoomable() {
    return this.mode === 'contain';
  }

  isMobileLandscapeZoomable() {
    return this.mode === 'contain';
  }

  canPinchZoom() {
    return this.mode === 'cover-height' || this.isLandscapeZoomable();
  }

  _updatePortraitPanBounds() {
    if (this.mode !== 'cover-height' && !this.isLandscapeZoomable()) return;
    if (this.isDesktop() && (this.trackedUnit || this.trackingProgress > 0.001)) return;
    const z = Math.max(0.0001, this.camera.zoom);
    const halfVisibleW = (this.cssW / z) / 2;
    this.panMinX = Math.min(this.worldW / 2, halfVisibleW);
    this.panMaxX = Math.max(this.worldW / 2, this.worldW - halfVisibleW);
    this.camera.x = Math.min(this.panMaxX, Math.max(this.panMinX, this.camera.x));
    if (this.isLandscapeZoomable()) {
      const span = Math.max(0.0001, this.zoomEmbedded - this.zoomOverview);
      const t = Math.max(0, Math.min(1, (z - this.zoomOverview) / span));
      this.camera.y = this.baseCameraY + (this.embeddedCameraY - this.baseCameraY) * t;
    }
  }

  // Returns 0.0 in Default Close state and 1.0 in the 50% Wide Overview state
  // (smoothly varying during pinch/snap transitions). Used by post-processing
  // to drive the wide-angle edge vignette and subtle lens-distortion warp.
  getWideZoomProgress() {
    if (this.mode !== 'cover-height') return 0;
    const span = this.zoomClose - this.zoomWide;
    if (span <= 0.0001) return 0;
    return Math.max(0, Math.min(1, (this.zoomClose - this.camera.zoom) / span));
  }

  resetPortraitZoom() {
    this.portraitZoomState = 0;
    this.landscapeZoomState = 0;
    this.isPinching = false;
    this._wheelZoomRatio = 1;
    if (this._wheelSnapTimer) {
      clearTimeout(this._wheelSnapTimer);
      this._wheelSnapTimer = null;
    }
    this._zoomAnchorClientX = null;
    this._landscapeFocusWorldX = null;
    if (this.mode === 'cover-height') {
      this.targetZoom = this.zoomClose;
      this.camera.zoom = this.zoomClose;
      this._updatePortraitPanBounds();
    } else if (this.isLandscapeZoomable() && (!this.isDesktop() || (!this.trackedUnit && this.trackingProgress <= 0.001))) {
      this.targetZoom = this.zoomOverview;
      this.camera.zoom = this.zoomOverview;
      this.camera.x = this.baseCameraX;
      this.camera.y = this.baseCameraY;
      this._updatePortraitPanBounds();
    }
  }

  setPortraitZoomState(stateIndex, anchorClientX = null) {
    if (!this.canPinchZoom()) return;
    if (this.isDesktop() && this.trackedUnit) {
      if (this.trackedUnit.__introZoomMult) {
        this.stopTrackingUnit();
        this.trackingProgress = 0;
      } else {
        return; // Keep desktop single-click unit tracking mode intact
      }
    }
    this.stopMobileIntro();
    this.isPinching = false;
    this._wheelZoomRatio = 1;
    if (this._wheelSnapTimer) {
      clearTimeout(this._wheelSnapTimer);
      this._wheelSnapTimer = null;
    }
    const nextState = stateIndex ? 1 : 0;
    if (this.isLandscapeZoomable()) {
      this.landscapeZoomState = nextState;
      this.targetZoom = (nextState === 1) ? this.zoomEmbedded : this.zoomOverview;
      this._zoomAnchorClientX = null;
      if (nextState === 1) {
        if (anchorClientX !== null && this.canvas) {
          const rect = this.canvas.getBoundingClientRect();
          const sxOffset = (anchorClientX - rect.left) - this.cssW / 2;
          this._landscapeFocusWorldX = this.camera.x + sxOffset / Math.max(0.0001, this.camera.zoom);
        } else {
          this._landscapeFocusWorldX = null;
        }
        this.userPanUntil = performance.now() + 2800;
      } else {
        this._landscapeFocusWorldX = this.worldW / 2;
        if (!this.isDesktop()) {
          this.stopTrackingUnit();
        }
      }
      return;
    }
    this.portraitZoomState = nextState;
    this.targetZoom = (this.portraitZoomState === 1) ? this.zoomWide : this.zoomClose;
    this._zoomAnchorClientX = anchorClientX;
  }

  setLandscapeZoomState(stateIndex, anchorClientX = null) {
    this.setPortraitZoomState(stateIndex, anchorClientX);
  }

  togglePortraitZoomState(anchorClientX = null) {
    if (!this.canPinchZoom()) return false;
    if (this.isDesktop() && this.trackedUnit && !this.trackedUnit.__introZoomMult) return false;
    const currentState = this.isLandscapeZoomable() ? this.landscapeZoomState : this.portraitZoomState;
    const nextState = currentState === 0 ? 1 : 0;
    this.setPortraitZoomState(nextState, anchorClientX);
    return true;
  }

  toggleLandscapeZoomState(anchorClientX = null) {
    return this.togglePortraitZoomState(anchorClientX);
  }

  startPinch(centerClientX = null) {
    if (!this.canPinchZoom()) return;
    if (this.isDesktop() && this.trackedUnit) {
      if (this.trackedUnit.__introZoomMult) {
        this.stopTrackingUnit();
        this.trackingProgress = 0;
      } else {
        return; // Keep desktop single-click unit tracking mode intact
      }
    }
    this.stopMobileIntro();
    this.isPinching = true;
    this._pinchStartZoom = this.camera.zoom;
    this._pinchStartState = this.isLandscapeZoomable() ? this.landscapeZoomState : this.portraitZoomState;
    this._pinchLastCenterClientX = centerClientX;
    this._zoomAnchorClientX = null;
    this._landscapeFocusWorldX = null;
  }

  updatePinch(scaleRatio, centerClientX = null) {
    if (!this.isPinching || !this.canPinchZoom()) return;
    const ratio = Number.isFinite(scaleRatio) && scaleRatio > 0 ? scaleRatio : 1;
    const rawZoom = this._pinchStartZoom * ratio;

    let nextZoom = rawZoom;
    if (this.isLandscapeZoomable()) {
      const minZ = this.zoomOverview;
      const maxZ = this.zoomEmbedded;
      // Landscape (Mobile Landscape & Desktop):
      // - Below minZ (zooming out past 100% Overview): gentle rubber-band resistance, snaps back to Overview.
      // - Between minZ (Overview) and maxZ (Embedded Lane View): 1:1 tracking.
      // - Above maxZ (pinching/scrolling IN closer than Embedded Lane View): smooth live zoom-in up to 1.65x, snaps back to Embedded Lane View on release.
      if (rawZoom < minZ) {
        nextZoom = Math.max(minZ * 0.94, minZ - (minZ - rawZoom) * 0.18);
      } else if (rawZoom > maxZ) {
        nextZoom = Math.min(maxZ * 1.65, maxZ + (rawZoom - maxZ) * 0.78);
      }
    } else {
      const minZ = this.zoomWide;
      const maxZ = this.zoomClose;
      // Mobile Portrait:
      // - Below minZ (zooming out past 50%): soft rubber-band resistance.
      // - Between minZ and maxZ: 1:1 tracking.
      // - Above maxZ (pinching IN closer than the Standard perspective): allows smooth
      //   live zoom-in up to 1.75x of Standard zoom, then snaps back to Standard (State 0) on release.
      if (rawZoom < minZ) {
        nextZoom = Math.max(minZ * 0.93, minZ - (minZ - rawZoom) * 0.22);
      } else if (rawZoom > maxZ) {
        nextZoom = Math.min(maxZ * 1.75, maxZ + (rawZoom - maxZ) * 0.78);
      }
    }

    // Keep the world point under the pinch/cursor center anchored while allowing pan
    let anchorWorldX = this.camera.x;
    let sxOffset = 0;
    if (centerClientX !== null && this.canvas) {
      const rect = this.canvas.getBoundingClientRect();
      const prevClientX = this._pinchLastCenterClientX !== null ? this._pinchLastCenterClientX : centerClientX;
      const prevSx = prevClientX - rect.left - this.cssW / 2;
      anchorWorldX = this.camera.x + prevSx / Math.max(0.0001, this.camera.zoom);
      sxOffset = (centerClientX - rect.left) - this.cssW / 2;
      this._pinchLastCenterClientX = centerClientX;
    }

    this.camera.zoom = nextZoom;
    this.targetZoom = nextZoom;
    if (centerClientX !== null) {
      this.camera.x = anchorWorldX - sxOffset / Math.max(0.0001, this.camera.zoom);
    }
    this._updatePortraitPanBounds();
    this.userPanUntil = performance.now() + 2500;
  }

  // Handles Desktop Mouse Wheel & Trackpad 2-Finger Pinch/Scroll zoom:
  // deltaY < 0 = zoom IN (Overview -> Embedded Lane View, or elastic close-up zoom-in that snaps back to Embedded Lane View);
  // deltaY > 0 = zoom OUT (Embedded Lane View -> Overview).
  handleWheelZoom(deltaY, deltaMode = 0, clientX = null) {
    if (!this.canPinchZoom()) return false;
    if (this.isDesktop() && this.trackedUnit) {
      if (this.trackedUnit.__introZoomMult) {
        this.stopTrackingUnit();
        this.trackingProgress = 0;
      } else {
        return false; // Keep desktop single-click unit tracking mode intact
      }
    }
    if (!Number.isFinite(deltaY) || Math.abs(deltaY) < 0.01) return false;

    let normDelta = deltaY;
    if (deltaMode === 1) normDelta *= 32;
    else if (deltaMode === 2) normDelta *= 300;
    normDelta = Math.max(-180, Math.min(180, normDelta));

    const stepFactor = Math.exp(-normDelta * 0.0038);
    if (!this.isPinching) {
      this.startPinch(clientX);
      this._wheelZoomRatio = 1;
    }
    this._wheelZoomRatio = Math.max(0.45, Math.min(2.6, (this._wheelZoomRatio || 1) * stepFactor));
    this.updatePinch(this._wheelZoomRatio, clientX);

    if (this._wheelSnapTimer) {
      clearTimeout(this._wheelSnapTimer);
    }
    this._wheelSnapTimer = setTimeout(() => {
      this._wheelSnapTimer = null;
      this._wheelZoomRatio = 1;
      this.endPinch();
    }, 180);
    return true;
  }

  endPinch() {
    if (this._wheelSnapTimer) {
      clearTimeout(this._wheelSnapTimer);
      this._wheelSnapTimer = null;
    }
    this._wheelZoomRatio = 1;

    if (!this.isPinching) {
      return this.isLandscapeZoomable() ? this.landscapeZoomState : this.portraitZoomState;
    }
    this.isPinching = false;

    if (this.isLandscapeZoomable()) {
      const span = Math.max(0.0001, this.zoomEmbedded - this.zoomOverview);
      // 0.0 = at Overview (State 0), 1.0 = at Embedded Lane View (State 1), >1.0 = zoomed in closer than State 1
      const embedFrac = (this.camera.zoom - this.zoomOverview) / span;
      let nextState = this._pinchStartState;
      if (this._pinchStartState === 0) {
        nextState = embedFrac >= 0.20 ? 1 : 0;
      } else {
        // When starting in State 1, pinching/scrolling IN further (embedFrac > 1.0) snaps back to State 1;
        // pinching/scrolling OUT by >=20% (embedFrac <= 0.80) snaps back to State 0 (Overview).
        nextState = embedFrac <= 0.80 ? 0 : 1;
      }
      this.landscapeZoomState = nextState;
      this.targetZoom = (nextState === 1) ? this.zoomEmbedded : this.zoomOverview;
      this._zoomAnchorClientX = this._pinchLastCenterClientX;
      this._pinchLastCenterClientX = null;
      if (nextState === 0) {
        this._landscapeFocusWorldX = this.worldW / 2;
        if (!this.isDesktop()) {
          this.stopTrackingUnit();
        }
      }
      return this.landscapeZoomState;
    }

    const span = Math.max(0.0001, this.zoomClose - this.zoomWide);
    // 0.0 = at Close, 1.0 = at Wide (~50%)
    const wideFrac = (this.zoomClose - this.camera.zoom) / span;
    // Directional hysteresis: 20% travel from the starting state triggers the snap
    let nextState = this._pinchStartState;
    if (this._pinchStartState === 0) {
      nextState = wideFrac >= 0.20 ? 1 : 0;
    } else {
      nextState = wideFrac <= 0.80 ? 0 : 1;
    }
    this.portraitZoomState = nextState;
    this.targetZoom = (nextState === 1) ? this.zoomWide : this.zoomClose;
    this._zoomAnchorClientX = this._pinchLastCenterClientX;
    this._pinchLastCenterClientX = null;
    return this.portraitZoomState;
  }

  updatePortraitZoom(dt) {
    if (this.isDesktop() && (this.trackedUnit || this.trackingProgress > 0.001)) return;
    const isLand = this.isLandscapeZoomable();
    if ((this.mode !== 'cover-height' && !isLand) || this.isPinching) return;
    const targetZ = isLand
      ? ((this.landscapeZoomState === 1) ? this.zoomEmbedded : this.zoomOverview)
      : ((this.portraitZoomState === 1) ? this.zoomWide : this.zoomClose);
    this.targetZoom = targetZ;
    const diff = targetZ - this.camera.zoom;
    if (Math.abs(diff) <= 0.0015) {
      if (this.camera.zoom !== targetZ) {
        this.camera.zoom = targetZ;
        if (isLand && this._landscapeFocusWorldX !== null) {
          this.camera.x = this._landscapeFocusWorldX;
        }
        this._updatePortraitPanBounds();
      }
      this._zoomAnchorClientX = null;
      this._landscapeFocusWorldX = null;
      return;
    }

    let anchorWorldX = null;
    let sxOffset = 0;
    if (this._zoomAnchorClientX !== null && this.canvas) {
      const rect = this.canvas.getBoundingClientRect();
      sxOffset = (this._zoomAnchorClientX - rect.left) - this.cssW / 2;
      anchorWorldX = this.camera.x + sxOffset / Math.max(0.0001, this.camera.zoom);
    }

    const zLerp = 1 - Math.exp(-Math.min(0.1, Math.max(0.001, dt)) * 16);
    this.camera.zoom += diff * zLerp;
    if (Math.abs(targetZ - this.camera.zoom) <= 0.0015) {
      this.camera.zoom = targetZ;
    }
    if (isLand && this._landscapeFocusWorldX !== null) {
      this.camera.x += (this._landscapeFocusWorldX - this.camera.x) * zLerp;
    } else if (anchorWorldX !== null) {
      this.camera.x = anchorWorldX - sxOffset / Math.max(0.0001, this.camera.zoom);
    }
    this._updatePortraitPanBounds();
  }

  canPan() {
    if (this.mode === 'cover-height') return true;
    if (this.isLandscapeZoomable()) {
      if (this.isDesktop() && (this.trackedUnit || this.trackingProgress > 0.001)) return false;
      return this.landscapeZoomState === 1 || this.isPinching || (this.panMaxX - this.panMinX) > 1.0;
    }
    return false;
  }

  // Returns the visible world-coordinate rectangle currently seen by the camera
  getVisibleBounds() {
    const scale = Math.max(0.0001, this.camera.zoom);
    const halfW = (this.cssW / scale) / 2;
    const halfH = (this.cssH / scale) / 2;
    return {
      left: this.camera.x - halfW,
      right: this.camera.x + halfW,
      top: this.camera.y - halfH,
      bottom: this.camera.y + halfH,
      width: this.cssW / scale,
      height: this.cssH / scale
    };
  }

  // Cheap per-frame safety net on top of the ResizeObserver: some browser
  // contexts (embedded webviews, certain in-app browsers, and - empirically
  // - this project's own automated test tooling) don't reliably fire
  // resize notifications on every layout change. A single
  // getBoundingClientRect() read is negligible next to everything else a
  // frame already does, so just checking it directly removes the
  // dependency on any particular event actually arriving.
  syncIfNeeded() {
    // Viewport-Reflows drosseln (v3 §3 P2 #2):
    // If ResizeObserver is active, clientWidth/clientHeight reads cause forced reflows
    // on DOM mutations. Throttle checks to every 30 frames when ResizeObserver is watching.
    if (this._ro) {
      this._syncThrottleCounter = (this._syncThrottleCounter || 0) + 1;
      if (this._syncThrottleCounter < 30) {
        return false;
      }
      this._syncThrottleCounter = 0;
    }

    const cw = this.canvas.clientWidth;
    const ch = this.canvas.clientHeight;
    if (cw > 0 && ch > 0 && (Math.abs(cw - this.cssW) > 1.5 || Math.abs(ch - this.cssH) > 1.5)) {
      this.resize();
      return true;
    }
    return false;
  }

  // Applies the world->device-pixel transform. Caller is responsible for
  // ctx.save()/ctx.restore() around this - see Renderer.render().
  applyTransform(ctx) {
    const scale = this.camera.zoom * this.dpr;
    ctx.setTransform(
      scale, 0, 0, scale,
      this.canvas.width / 2 - this.camera.x * scale,
      this.canvas.height / 2 - this.camera.y * scale
    );
  }

  // clientX/clientY are raw pointer-event coordinates (viewport space).
  screenToWorld(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    return {
      x: (sx - this.cssW / 2) / this.camera.zoom + this.camera.x,
      y: (sy - this.cssH / 2) / this.camera.zoom + this.camera.y
    };
  }

  // Returns CSS-pixel coordinates relative to the canvas element's own
  // box (i.e. add the canvas's own offset to place in a shared ancestor).
  worldToScreen(x, y) {
    return {
      x: this.cssW / 2 + (x - this.camera.x) * this.camera.zoom,
      y: this.cssH / 2 + (y - this.camera.y) * this.camera.zoom
    };
  }

  // dxCss/dyCss: pointer movement delta in CSS pixels since the last call.
  panBy(dxCss) {
    if (!this.canPan()) return;
    this.stopMobileIntro();
    this.camera.x -= dxCss / this.camera.zoom;
    this.camera.x = Math.min(this.panMaxX, Math.max(this.panMinX, this.camera.x));
    this.userPanUntil = performance.now() + 4000;
  }

  // Absolute jump to a world X (e.g. a tap/drag on the minimap - see
  // js/ui/minimap.js), as opposed to panBy's relative on-field drag delta.
  // Counts as a manual pan for auto-follow suspension purposes too.
  jumpTo(worldX) {
    if (!this.canPan()) return;
    this.stopMobileIntro();
    this.camera.x = Math.min(this.panMaxX, Math.max(this.panMinX, worldX));
    this.userPanUntil = performance.now() + 4000;
  }

  resetFollow() {
    this._lastFollowTargetX = undefined;
    this._targetDirection = 0;
    this._smoothDirection = 0;
    this._directionSustainTimer = 0;
    this._directionOpposingDistance = 0;
    this._stationaryTimer = 0;
    this._combatHoldTimer = 0;
    this._combatWeight = 0;
  }

  // Smoothly frames the camera on the frontline with advance look-ahead (March Mode),
  // and blends into combat framing when engagements occur so both combat parties
  // are visible simultaneously without jarring camera jumps (Option B Hybrid).
  // Accepts either a raw targetX number or the framing descriptor from
  // Battlefield.getCameraFramingInfo().
  followX(targetOrFraming, dt) {
    if (!this.canPan()) return;

    const isFramingObj = (typeof targetOrFraming === 'object' && targetOrFraming !== null);
    const targetX = isFramingObj ? (targetOrFraming.focalX ?? (this.worldW / 2)) : targetOrFraming;
    const inCombat = isFramingObj ? !!targetOrFraming.inCombat : false;

    if (this._lastFollowTargetX === undefined) {
      this._lastFollowTargetX = targetX;
      this._targetDirection = 0;
      this._smoothDirection = 0;
      this._directionSustainTimer = 0;
      this._directionOpposingDistance = 0;
      this._stationaryTimer = 0;
      this._combatHoldTimer = 0;
      this._combatWeight = 0;
    }

    const rawDelta = targetX - this._lastFollowTargetX;
    this._lastFollowTargetX = targetX;

    // 1. DIRECTION HYSTERESIS & ADVANCE FILTER
    // Keeps the advance direction stable even during skirmishes.
    // The look-ahead is sustained as long as the army is advancing;
    // only if the frontline is held static for > 2.0s does look-ahead decay to center.
    if (Math.abs(rawDelta) > 0.05) {
      this._stationaryTimer = 0;
      const candidateDirection = Math.sign(rawDelta);
      if (candidateDirection !== this._targetDirection) {
        this._directionSustainTimer = (this._directionSustainTimer || 0) + dt;
        this._directionOpposingDistance = (this._directionOpposingDistance || 0) + Math.abs(rawDelta);
        if (this._directionSustainTimer >= 0.6 || this._directionOpposingDistance >= 50) {
          this._targetDirection = candidateDirection;
          this._directionSustainTimer = 0;
          this._directionOpposingDistance = 0;
        }
      } else {
        this._directionSustainTimer = 0;
        this._directionOpposingDistance = 0;
      }
    } else {
      this._directionSustainTimer = 0;
      this._directionOpposingDistance = 0;
      this._stationaryTimer = (this._stationaryTimer || 0) + dt;
      // If the frontline is held static for > 2.0s, gently relax look-ahead to center
      if (this._stationaryTimer > 2.0) {
        this._targetDirection = 0;
      }
    }

    if (performance.now() < this.userPanUntil) return;

    // 2. CONTINUOUS DIRECTIONAL BLEND (Cinematic Ease)
    // Seamlessly eases _smoothDirection between -1.0, 0.0, and +1.0 over ~1.0s.
    if (this._smoothDirection === undefined) this._smoothDirection = 0;
    const dirLerp = 1 - Math.pow(0.02, dt);
    this._smoothDirection += (this._targetDirection - this._smoothDirection) * dirLerp;

    const visibleW = this.cssW / Math.max(0.0001, this.camera.zoom);
    const halfVisibleW = visibleW / 2;

    // 3. CONTINUOUS MARCH BASELINE (Vorhalt / Look-Ahead in travel direction)
    const wideProg = this.getWideZoomProgress();
    const shiftFraction = 0.55 - wideProg * 0.25;
    const shift = this._smoothDirection * shiftFraction * halfVisibleW;
    const marchTargetX = targetX + shift;

    // 4. COMBAT SUSTAIN (Option B Hybrid)
    // A 1.2s sustain timer prevents flickering when targets die, attacks reload, or units reposition.
    if (inCombat) {
      this._combatHoldTimer = 1.2;
    } else if (this._combatHoldTimer > 0) {
      this._combatHoldTimer = Math.max(0, this._combatHoldTimer - dt);
    }
    const combatActive = inCombat || (this._combatHoldTimer > 0);

    // 5. COMBAT FRAMING ADJUSTMENT (Smart Lock & Comfort Window)
    if (isFramingObj && combatActive) {
      this._combatWeight = 1.0;
      const leftX = targetOrFraming.leftCombatX;
      const rightX = targetOrFraming.rightCombatX;
      const attackerX = targetOrFraming.attackerX ?? leftX;
      const defenderX = targetOrFraming.defenderX ?? rightX;
      const COMBAT_PAD = 65; // World-px clearance so unit sprites and health bars stay inside frame

      const reqLeft = leftX - COMBAT_PAD;
      const reqRight = rightX + COMBAT_PAD;
      const reqSpan = reqRight - reqLeft;

      let combatAdjustedX = this.camera.x;
      if (reqSpan <= visibleW) {
        // Both parties can fit inside the viewport simultaneously.
        // Any camera position in [validMinCamX, validMaxCamX] shows BOTH reqLeft and reqRight.
        const validMinCamX = reqRight - halfVisibleW;
        const validMaxCamX = reqLeft + halfVisibleW;
        const combatMidX = (leftX + rightX) * 0.5;
        const innerBuffer = Math.min(24, Math.max(0, (validMaxCamX - validMinCamX) * 0.25));
        const safeMin = validMinCamX + innerBuffer;
        const safeMax = validMaxCamX - innerBuffer;

        if (this.camera.x < safeMin) {
          // Right combat party is off-screen or too close to the right edge -> glide right
          combatAdjustedX = Math.min(combatMidX, safeMin + 18);
        } else if (this.camera.x > safeMax) {
          // Left combat party is off-screen or too close to the left edge -> glide left
          combatAdjustedX = Math.max(combatMidX, safeMax - 18);
        } else {
          // Both parties are comfortably inside the comfort window -> Smart Lock holds camera steady!
          combatAdjustedX = this.camera.x;
        }
      } else {
        // Combat span exceeds visible width (long-range siege):
        // Anchor the attacker comfortably near the trailing edge and look toward defender.
        if (defenderX >= attackerX) {
          combatAdjustedX = (attackerX - COMBAT_PAD) + halfVisibleW;
        } else {
          combatAdjustedX = (attackerX + COMBAT_PAD) - halfVisibleW;
        }
      }

      const clampedCombatX = Math.min(this.panMaxX, Math.max(this.panMinX, combatAdjustedX));
      // Calmer pan speed in combat so the camera glides gently toward the defender
      const combatCamLerp = 1 - Math.pow(0.08, dt);
      this.camera.x += (clampedCombatX - this.camera.x) * combatCamLerp;
      return;
    }

    // 6. MARCH MODE (Standard advance tracking outside of combat)
    const clamped = Math.min(this.panMaxX, Math.max(this.panMinX, marchTargetX));
    const camLerp = 1 - Math.pow(0.005, dt);
    this.camera.x += (clamped - this.camera.x) * camLerp;
  }

  isCoarse() {
    if (this._coarse === undefined) {
      this._coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    }
    return !!this._coarse;
  }

  isDesktop() {
    return !this.isPortrait() && !this.isCoarse();
  }

  isMobileLandscape() {
    if (this.isPortrait()) return false;
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      if (window.matchMedia('(orientation: landscape) and (max-height: 560px)').matches) return true;
      if (this.isCoarse() && window.matchMedia('(orientation: landscape) and (max-height: 580px)').matches) return true;
    }
    return !this.isPortrait() && (this.cssH <= 560 || (this.isCoarse() && this.cssH <= 580));
  }

  // Coarse-pointer devices (touch) get a bigger effective hit radius so a
  // finger can reliably land on a unit/building even after zoom shrinks it
  // on screen - see MOBILE_PLAN.md section 8.
  hitRadiusBonus() {
    if (this._coarse === undefined) {
      this._coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    }
    return this._coarse ? Math.max(15, 22 / this.camera.zoom) : 15;
  }

  // Tracking itself (this.trackedUnit) is device-agnostic - both desktop
  // (updateUnitTracking, zoom+follow) and mobile (updateMobileUnitFollow,
  // pan-only) read it, each applying their own camera treatment below.
  startTrackingUnit(unit) {
    if (!unit) return;
    this.stopMobileIntro();
    if (this.isDesktop()) {
      this.landscapeZoomState = 0;
      this.isPinching = false;
      this._wheelZoomRatio = 1;
      if (this._wheelSnapTimer) {
        clearTimeout(this._wheelSnapTimer);
        this._wheelSnapTimer = null;
      }
      if (this.zoomOverview !== undefined) {
        this.targetZoom = this.zoomOverview;
      }
    }
    this.trackedUnit = unit;
    this._deadLingerTimer = 0;
    // Remembered past stopTrackingUnit() (which nulls trackedUnit) so the
    // release-phase lerp below still knows to ease out slowly - see
    // updateUnitTracking()'s camLerp selection.
    this._slowRelease = !!unit.__introZoomMult;
  }

  stopTrackingUnit() {
    this.trackedUnit = null;
    this._deadLingerTimer = 0;
    // Otherwise followX() would see a stale _lastFollowTargetX from before
    // tracking started and read the jump back to the frontline as a single
    // huge rawDelta, briefly confusing its direction hysteresis.
    this.resetFollow();
  }

  // Desktop-exclusive unit tracking: smoothly zooms into the clicked unit and follows
  // its position across the combat lane ("spielfeld-höhe"). Mobile tracking
  // (same this.trackedUnit, set by the same click) is handled separately by
  // updateMobileUnitFollow() below, without any zoom.
  updateUnitTracking(dt) {
    if (!this.isDesktop()) return;

    if (this.trackedUnit) {
      if (this.trackedUnit.isDead) {
        this._deadLingerTimer = (this._deadLingerTimer || 0) + dt;
        if (this._deadLingerTimer >= 0.8) {
          this.stopTrackingUnit();
        }
      } else {
        this._deadLingerTimer = 0;
      }
    }

    const isTracking = !!this.trackedUnit;
    const targetProgress = isTracking ? 1.0 : 0.0;

    if (!isTracking && this.trackingProgress <= 0.0001) {
      this.trackingProgress = 0;
      return;
    }

    let targetZoom = this.baseZoom;
    let targetX = this.baseCameraX !== undefined ? this.baseCameraX : this.worldW / 2;
    let targetY = this.baseCameraY !== undefined ? this.baseCameraY : this.worldH / 2;

    if (isTracking) {
      // Zoom factor: 1.85x baseZoom frames the unit and combat lane comfortably.
      // The match-start base intro (see Game._startCameraIntro() in
      // js/main.js) opts into a tighter override via these two optional
      // fields on the tracked object - the base sits low in the lane
      // (y=340 vs. world-center 270), and at the standard 1.85x zoom the
      // vertical clamp below has almost no headroom to lift it clear of the
      // bottom HUD before hitting the world's bottom edge, so it still hugs
      // the HUD. A tighter zoom widens that headroom; __introPushMaxY then
      // deliberately frames at the clamp ceiling instead of the object's
      // raw y, maximizing how far up the base sits without ever exposing
      // blank space past the map's actual bottom edge. Regular unit
      // click-tracking never sets these, so its framing is unchanged.
      targetZoom = this.baseZoom * (this.trackedUnit.__introZoomMult || 1.85);

      const ux = this.trackedUnit.x;
      const uy = this.trackedUnit.isAir ? (this.trackedUnit.y - 18) : this.trackedUnit.y;

      const currentZ = Math.max(0.0001, this.camera.zoom);
      const halfVisW = (this.cssW / currentZ) / 2;
      const halfVisH = (this.cssH / currentZ) / 2;

      const minX = Math.min(this.worldW / 2, halfVisW);
      const maxX = Math.max(this.worldW / 2, this.worldW - halfVisW);
      const minY = Math.min(this.worldH / 2, halfVisH);
      const maxY = Math.max(this.worldH / 2, this.worldH - halfVisH);

      targetX = Math.max(minX, Math.min(maxX, ux));
      // maxY itself puts the base's own y right at the visible top edge
      // (top edge = maxY - halfVisH = worldH - 2*halfVisH + ... which for
      // this map's dimensions lands almost exactly on the base's y=340),
      // clipping the top of its sprite. Backing off a few px gives it
      // breathing room without giving up meaningful headroom.
      targetY = this.trackedUnit.__introPushMaxY ? Math.max(minY, maxY - 24) : Math.max(minY, Math.min(maxY, uy));
    }

    // Smooth exponential ease. The match-start base intro's release back to
    // the resting overview (isTracking false, _slowRelease set by
    // startTrackingUnit above) uses a gentler base than the regular
    // zoom-in/unit-click-release rate - at 0.004 the release was basically
    // done within half a second, reading as an abrupt snap rather than a
    // deliberate pull-back.
    const easeBase = (!isTracking && this._slowRelease) ? 0.1 : 0.004;
    const camLerp = 1 - Math.pow(easeBase, dt);
    this.camera.x += (targetX - this.camera.x) * camLerp;
    this.camera.y += (targetY - this.camera.y) * camLerp;
    this.camera.zoom += (targetZoom - this.camera.zoom) * camLerp;
    this.trackingProgress += (targetProgress - this.trackingProgress) * camLerp;

    if (!isTracking && this.trackingProgress < 0.001) {
      this.trackingProgress = 0;
      this.camera.x = targetX;
      this.camera.y = targetY;
      this.camera.zoom = targetZoom;
      this._slowRelease = false;
    }
  }

  // Mobile-exclusive unit tracking: pans the camera horizontally to the
  // clicked unit, mirroring desktop's click-to-follow but with no zoom -
  // mobile's field is already scaled to fill the screen height ('cover-height'
  // mode), so there's nothing to zoom into, only across. A no-op outside
  // portrait (mode 'contain' has no panning at all - whole field visible).
  // Returns true while it owns the camera this frame, so the caller knows to
  // skip the frontline auto-follow (followX) instead of fighting it.
  updateMobileUnitFollow(dt) {
    if (this.isDesktop() || !this.trackedUnit) return false;

    if (this.trackedUnit.isDead) {
      this._deadLingerTimer = (this._deadLingerTimer || 0) + dt;
      if (this._deadLingerTimer >= 0.8) {
        this.stopTrackingUnit();
        return false;
      }
    } else {
      this._deadLingerTimer = 0;
    }

    if (!this.canPan()) return true;

    const targetX = Math.min(this.panMaxX, Math.max(this.panMinX, this.trackedUnit.x));
    const camLerp = 1 - Math.pow(0.004, dt);
    this.camera.x += (targetX - this.camera.x) * camLerp;
    return true;
  }

  // Match-start mobile intro: frames the player's own base for ~1s, then slowly
  // and smoothly wanders to the battlefield center before handing off to normal auto-follow.
  startMobileIntro(startX, targetX = this.worldW / 2, holdDuration = 1.0, panDuration = 2.2) {
    if (!this.canPan()) return;
    this.resetPortraitZoom();
    const clampedStart = Math.min(this.panMaxX, Math.max(this.panMinX, startX));
    const clampedTarget = Math.min(this.panMaxX, Math.max(this.panMinX, targetX));
    this.camera.x = clampedStart;
    this.mobileIntro = {
      startX: clampedStart,
      targetX: clampedTarget,
      holdDuration,
      panDuration,
      elapsed: 0
    };
    this.resetFollow();
  }

  stopMobileIntro() {
    if (this.mobileIntro) {
      this.mobileIntro = null;
      this.resetFollow();
    }
  }

  updateMobileIntro(dt) {
    if (!this.mobileIntro) return false;
    if (performance.now() < this.userPanUntil) {
      this.stopMobileIntro();
      return false;
    }
    this.mobileIntro.elapsed += dt;
    const { startX, targetX, holdDuration, panDuration, elapsed } = this.mobileIntro;

    if (elapsed <= holdDuration) {
      this.camera.x = startX;
      return true;
    }

    const panElapsed = elapsed - holdDuration;
    if (panElapsed >= panDuration) {
      this.camera.x = targetX;
      this.stopMobileIntro();
      return false;
    }

    const t = panElapsed / panDuration;
    // Smootherstep (Ken Perlin): t^3 * (t * (6t - 15) + 10) for zero start/end velocity
    const ease = t * t * t * (t * (t * 6 - 15) + 10);
    this.camera.x = startX + (targetX - startX) * ease;
    return true;
  }
}
